<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next';
import { computed } from 'vue';
import { buttonBaseClass, buttonSizeClass, buttonVariantClass } from './button';

export interface ButtonProps {
    variant?: 'primary' | 'secondary' | 'ghost' | 'destructive';
    size?: 'sm' | 'md' | 'lg' | 'icon';
    loading?: boolean;
    disabled?: boolean;
    /**
     * Opt-in : désactivé mais toujours focalisable (`aria-disabled`), pour un bouton dont
     * la raison est lue par `aria-describedby`. Rendu désactivé, clic neutralisé.
     */
    softDisabled?: boolean;
    /**
     * Avec `softDisabled` : le clic reste émis, pour un bouton dont le clic doit expliquer
     * pourquoi l'action est bloquée (le parent décide). Sans effet sur `loading`.
     */
    softDisabledClick?: boolean;
    type?: 'button' | 'submit' | 'reset';
}

const props = withDefaults(defineProps<ButtonProps>(), {
    variant: 'primary',
    size: 'md',
    loading: false,
    disabled: false,
    softDisabled: false,
    softDisabledClick: false,
    type: 'button',
});

const emit = defineEmits<{
    (e: 'click', event: MouseEvent): void;
}>();

const variantClasses = computed(() => buttonVariantClass(props.variant));

const sizeClasses = computed(() => buttonSizeClass(props.size));

/** En chargement, le survol et l'appui ne doivent pas suggérer un bouton actif. */
const stateClasses = computed(() =>
    props.loading || props.softDisabled
        ? variantClasses.value
              .split(' ')
              .filter(
                  (name) =>
                      !name.startsWith('hover:') && !name.startsWith('active:'),
              )
              .join(' ')
        : variantClasses.value,
);

const classes = computed(() => [
    buttonBaseClass,
    stateClasses.value,
    sizeClasses.value,
    props.loading && 'cursor-wait opacity-50',
    props.softDisabled && !props.loading && 'cursor-not-allowed opacity-50',
]);

/**
 * En chargement, le bouton reste focalisable (`aria-disabled`, pas `disabled`) :
 * un bouton focalisé qui devient `disabled` perd le focus au profit de <body>,
 * et le focus ne revient pas dans la modale après une erreur 422. Le clic (et la
 * soumission implicite d'un formulaire, qui passe par un clic) est neutralisé,
 * y compris quand le parent passe aussi `disabled` pendant le chargement.
 */
function handleClick(event: MouseEvent) {
    if (props.loading || (props.softDisabled && !props.softDisabledClick)) {
        event.preventDefault();
        return;
    }
    if (!props.disabled) {
        emit('click', event);
    }
}
</script>

<template>
    <button
        :type="type"
        :class="classes"
        :disabled="disabled && !loading"
        :aria-disabled="loading || softDisabled || undefined"
        @click="handleClick"
        :aria-busy="loading"
    >
        <Loader2
            v-if="loading"
            class="h-4 w-4 animate-spin"
            aria-hidden="true"
        />
        <slot />
        <span v-if="loading && !$slots.default" class="sr-only">{{
            $t('common.loading')
        }}</span>
    </button>
</template>
